/**
 * ComprobanteAportePanel — visor del comprobante de aporte que el asociado
 * sube desde la app móvil (estado pendiente).
 *
 * El path viene en `asociado.comprobante_aporte_url` (formato
 * `aportes/{asociado_id}/{ts}.jpg`). El bucket `kyc/` es privado, así que
 * pedimos un signed URL con TTL de 5 minutos para previsualizar.
 *
 * Se muestra en la fiche del asociado cuando estado es `pendiente`. El
 * operador puede:
 *   - Ver la foto del comprobante para verificar contra el extracto bancario.
 *   - Abrir en pestaña nueva (zoom + descarga).
 *   - Avanzar a "Registrar pago de inscripción" para activar al asociado.
 *
 * Si `comprobante_aporte_url` está null pero el estado es `pendiente`, eso
 * significa que el asociado eligió enviar por WhatsApp / canal off-band —
 * mostramos un banner en lugar de la foto.
 */
import { useEffect, useState } from 'react'
import {
  ArrowSquareOut,
  ChatCircleDots,
  ImageBroken,
  Receipt,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type Props = {
  comprobantePath: string | null
}

export function ComprobanteAportePanel({ comprobantePath }: Props) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!comprobantePath) return
    let cancelled = false
    setLoading(true)
    setError(null)
    supabase.storage
      .from('kyc')
      .createSignedUrl(comprobantePath, 300) // 5 min TTL
      .then(({ data, error: err }) => {
        if (cancelled) return
        if (err || !data) {
          setError(err?.message ?? 'No pudimos generar la URL del comprobante.')
          setSignedUrl(null)
        } else {
          setSignedUrl(data.signedUrl)
        }
      })
      .catch((e) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [comprobantePath])

  // Caso 1: NO subió comprobante por la app — eligió WhatsApp.
  if (!comprobantePath) {
    return (
      <div className="card" style={cardStyle}>
        <div style={headerStyle}>
          <div style={iconWrap}>
            <ChatCircleDots size={18} weight="duotone" color="#0F8A57" />
          </div>
          <div>
            <div style={titleStyle}>Comprobante por canal externo</div>
            <div style={hintStyle}>
              El asociado no subió comprobante por la app. Probablemente lo
              envió por WhatsApp u otro canal. Revisá ese canal antes de
              registrar el pago.
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card" style={cardStyle}>
      <div style={headerStyle}>
        <div style={iconWrap}>
          <Receipt size={18} weight="duotone" color="#F04E23" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={titleStyle}>Comprobante de aporte</div>
          <div style={hintStyle}>
            El asociado lo subió desde la app móvil. Verificá contra el
            extracto bancario antes de registrar el pago.
          </div>
        </div>
        {signedUrl && (
          <a
            href={signedUrl}
            target="_blank"
            rel="noreferrer"
            className="btn btn-ghost"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <ArrowSquareOut size={13} weight="bold" />
            Abrir
          </a>
        )}
      </div>

      <div style={imageBoxStyle}>
        {loading ? (
          <div style={placeholderStyle}>Cargando comprobante…</div>
        ) : error ? (
          <div style={{ ...placeholderStyle, color: '#dc2626', flexDirection: 'column', gap: 8 }}>
            <ImageBroken size={28} weight="duotone" />
            <span>{error}</span>
          </div>
        ) : signedUrl ? (
          // eslint-disable-next-line jsx-a11y/img-redundant-alt
          <img
            src={signedUrl}
            alt="Comprobante de aporte"
            style={imageStyle}
          />
        ) : null}
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Inline styles — alineados con el sistema actual (no hay tokens de Tailwind acá)
// -----------------------------------------------------------------------------

const cardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  padding: 16,
  background: '#FFFFFF',
  border: '1px solid rgba(17,17,17,0.08)',
  borderRadius: 14,
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
}

const iconWrap: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 16,
  background: 'rgba(240,78,35,0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}

const titleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#111111',
  letterSpacing: -0.1,
}

const hintStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6B6B6B',
  marginTop: 2,
  lineHeight: 1.45,
}

const imageBoxStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 200,
  maxHeight: 480,
  background: 'rgba(17,17,17,0.04)',
  borderRadius: 10,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const imageStyle: React.CSSProperties = {
  maxWidth: '100%',
  maxHeight: 480,
  objectFit: 'contain',
}

const placeholderStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#A8A8A8',
  padding: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}
